import DragStateManager from "../../utils/DragStateManager";
import { RulerLineType, SingleLineType, WallConnectionEnd, WallConnectionStart, WallConnectionMiddle, WallType, isWallType, isSingleLineType } from "../../types/floorplannerTypes";
import * as THREE from 'three';
import { closestPointOnLine } from "./closestPointOnLine";
import { floorplannerStore, validateDragDelta, validateWallDimensions, MAX_DRAG_DELTA } from '../../store/floorplannerStore';
import { editorStore } from "../../store/editorStore";
import { updateAlignmentLines } from "./updateAligmentLines";
import { runInAction } from "mobx";
import { wallUpdateManager } from "../../utils/wallUpdateManager";
import { renderStore } from "../../store/renderStore";
import { cascadeCenterConnectionUpdates, isMiddleHandleMovementBlocked, updateSymbolAttachmentsDuringDrag } from "./updateWallConnections";
import { getConnectionPoint } from "../../utils/connectionGeometry";
import { calculateAlignmentSnap } from "../../store/snapToOtherObjects";

/**
 * Validates that wall movement doesn't break center connections
 * @param wall The wall being moved
 * @param movedStart The proposed new start position
 * @param movedEnd The proposed new end position
 * @returns Object with clamped positions if constraints are needed, or null if no constraints
 */
export const validateCenterConnectionConstraints = (
    wall: WallType | SingleLineType | RulerLineType,
    movedStart: THREE.Vector2,
    movedEnd: THREE.Vector2
): { start: THREE.Vector2; end: THREE.Vector2 } | null => {
    // Only check constraints for walls or single lines with center connections
    if ((!isWallType(wall) && !isSingleLineType(wall)) || !wall.centerConnections || wall.centerConnections.length === 0) {
        return null;
    }

    // Calculate wall direction and perpendicular
    const wallDirection = new THREE.Vector2()
        .subVectors(movedEnd, movedStart)
        .normalize();
    const perpendicular = new THREE.Vector2(-wallDirection.y, wallDirection.x);

    let constrainedStart = movedStart.clone();
    let constrainedEnd = movedEnd.clone();
    let hasConstraint = false;

    // Check each center connection
    for (const connection of wall.centerConnections) {
        // Only validate constraints for middle connections (not edge connections)
        if (connection.sourcePosition === WallConnectionStart || connection.sourcePosition === WallConnectionEnd) {
            continue;
        }

        if (connection.targetPosition === WallConnectionStart || connection.targetPosition === WallConnectionEnd) {
            continue;
        }

        // Get the connected object (wall or single line)
        const connectedObject = floorplannerStore.findObjectId(connection.id);
        if (!connectedObject || (!isWallType(connectedObject) && !isSingleLineType(connectedObject))) {
            continue;
        }

        // Calculate the connection point on this wall/line (where it connects to the other object)
        // Use getConnectionPoint which handles WallConnectionMiddle (5000) correctly
        const connectionPoint = getConnectionPoint(movedStart, movedEnd, connection.sourcePosition);

        // Calculate the valid range along the perpendicular direction
        // The connection point must stay within the bounds of the connected object
        const connectedObjectDirection = new THREE.Vector2()
            .subVectors(connectedObject.end, connectedObject.start)
            .normalize();

        // Project the connection point onto the connected object to find the closest point
        const toConnectionPoint = new THREE.Vector2()
            .subVectors(connectionPoint, connectedObject.start);
        const projection = toConnectionPoint.dot(connectedObjectDirection);
        const connectedObjectLength = connectedObject.start.distanceTo(connectedObject.end);

        // Calculate valid range for the connection point along the perpendicular direction
        // This ensures the connection stays on the connected object
        const connectedObjectNormal = new THREE.Vector2(-connectedObjectDirection.y, connectedObjectDirection.x);

        // Calculate the range of valid positions for the connection point
        // For walls: use wall width, for single lines: use small tolerance (lines have no width)
        let maxOffset: number;
        if (isWallType(connectedObject)) {
            const wallWidth = (wall as WallType).wallWidth || 10;
            maxOffset = wallWidth / 2 + 5; // Add small tolerance
        } else {
            // For single lines, use a small tolerance since they have no width
            maxOffset = 0.001; // Very small tolerance for lines
        }

        // For each possible position of the connection on the connected object (0 to connectedObjectLength)
        // Check if our connection point would be too far from the connected object
        const closestPointOnConnectedObject = new THREE.Vector2().addVectors(
            connectedObject.start,
            connectedObjectDirection.clone().multiplyScalar(Math.max(0, Math.min(projection, connectedObjectLength)))
        );

        const distanceFromConnectedObject = new THREE.Vector2()
            .subVectors(connectionPoint, closestPointOnConnectedObject)
            .dot(connectedObjectNormal);

        // If the connection point is too far from the connected object, we need to constrain the movement
        if (Math.abs(distanceFromConnectedObject) > maxOffset) {
            // Calculate the constraint - project back onto the valid range
            const correction = Math.sign(distanceFromConnectedObject) * (maxOffset - Math.abs(distanceFromConnectedObject));
            
            // Validate correction is reasonable (should be small, not extreme)
            if (Math.abs(correction) > MAX_DRAG_DELTA) {
                console.error(`validateCenterConnectionConstraints: Correction ${correction} exceeds maximum ${MAX_DRAG_DELTA}m`);
                console.error('validateCenterConnectionConstraints: Constraint debug info:', {
                    wallId: wall.id,
                    connectionId: connection.id,
                    distanceFromConnectedObject,
                    maxOffset,
                    correction,
                    connectionPoint: { x: connectionPoint.x, y: connectionPoint.y },
                    closestPointOnConnectedObject: { x: closestPointOnConnectedObject.x, y: closestPointOnConnectedObject.y },
                });
                // Skip this constraint - it would produce extreme values
                continue;
            }
            
            const adjustment = connectedObjectNormal.clone().multiplyScalar(correction);

            constrainedStart.add(adjustment);
            constrainedEnd.add(adjustment);
            hasConstraint = true;
        }
    }

    return hasConstraint ? { start: constrainedStart, end: constrainedEnd } : null;
};

/**
 * Drag the middle handle of the object vertically
 * @param object The object to drag
 * @param startOffsetX The start x offset of the object
 * @param startOffsetY The start y offset of the object
 * @param newX The new x position of the object
 * @param newY The new y position of the object
 * @param delta The delta vector
 * @returns boolean If the object was snapped
 */
export const dragMiddleVHandle = (
    source: WallType | SingleLineType | RulerLineType,
    startOffsetX: number,
    startOffsetY: number,
    newX: number,
    newY: number,
    delta: THREE.Vector2,
    suppressSnapping: boolean = false,
    visualFeedbackMode: boolean = false,
): boolean => {
    const object = floorplannerStore.findObjectId(source.id);
    if (!object) {
        return false;
    }

    const wallDirection = new THREE.Vector2()
        .subVectors(object.end, object.start);
    if (wallDirection.lengthSq() > 0) {
        wallDirection.normalize();
    }
    const perpendicular = new THREE.Vector2(
        -wallDirection.y,
        wallDirection.x,
    );

    // MiddleVHandle constraints for walls and single lines with connections
    if (isWallType(object) || isSingleLineType(object)) {
        if (isMiddleHandleMovementBlocked(object as WallType | SingleLineType, perpendicular)) {
            return false;
        }
    }

    const midPoint = new THREE.Vector2(
        (object.start.x + object.end.x) / 2,
        (object.start.y + object.end.y) / 2,
    );
    let newDelta = new THREE.Vector2(
        newX - startOffsetX,
        newY - startOffsetY,
    ).sub(midPoint);
    const distance = newDelta.dot(perpendicular);
    newDelta = perpendicular.clone().multiplyScalar(distance);


    // Ensure the delta is large enough to matter (avoid floating point precision issues)
    const MIN_DELTA = 0.001;
    if (Math.abs(newDelta.x) < MIN_DELTA) newDelta.x = 0;
    if (Math.abs(newDelta.y) < MIN_DELTA) newDelta.y = 0;

    // Validate that the delta is within reasonable bounds
    // This catches bugs that could cause walls to be moved to extreme positions
    const deltaValidation = validateDragDelta(newDelta);
    if (!deltaValidation.isValid) {
        console.error(`dragMiddleVHandle: ${deltaValidation.error}`);
        console.error('dragMiddleVHandle: Debug info:', {
            wallId: object.id,
            newX, newY,
            startOffsetX, startOffsetY,
            midPoint: { x: midPoint.x, y: midPoint.y },
            perpendicular: { x: perpendicular.x, y: perpendicular.y },
            distance,
            rawDelta: { x: newDelta.x, y: newDelta.y },
            currentStart: { x: object.start.x, y: object.start.y },
            currentEnd: { x: object.end.x, y: object.end.y },
        });
        // Clamp the delta to a safe maximum to prevent complete corruption
        const magnitude = newDelta.length();
        if (magnitude > MAX_DRAG_DELTA) {
            newDelta.normalize().multiplyScalar(MAX_DRAG_DELTA);
            console.warn(`dragMiddleVHandle: Clamped delta to ${MAX_DRAG_DELTA}m`);
        }
    }

    // Apply delta to wall positions
    const movedStart = object.start.clone().add(newDelta);
    const movedEnd = object.end.clone().add(newDelta);



    // Validate center connection constraints to prevent breaking connections during middle handle drags
    const constrainedPositions = validateCenterConnectionConstraints(object, movedStart, movedEnd);
    let finalMovedStart = constrainedPositions ? constrainedPositions.start : movedStart;
    let finalMovedEnd = constrainedPositions ? constrainedPositions.end : movedEnd;

    // Validate that constraint validation didn't produce extreme values
    const constraintValidation = validateWallDimensions(finalMovedStart, finalMovedEnd);
    if (!constraintValidation.isValid) {
        console.error(`dragMiddleVHandle: Constraint validation produced invalid positions: ${constraintValidation.error}`);
        console.error('dragMiddleVHandle: Constraint debug info:', {
            wallId: object.id,
            movedStart: { x: movedStart.x, y: movedStart.y },
            movedEnd: { x: movedEnd.x, y: movedEnd.y },
            constrainedStart: constrainedPositions ? { x: constrainedPositions.start.x, y: constrainedPositions.start.y } : null,
            constrainedEnd: constrainedPositions ? { x: constrainedPositions.end.x, y: constrainedPositions.end.y } : null,
        });
        // Reset to original moved positions (before constraints)
        finalMovedStart = movedStart.clone();
        finalMovedEnd = movedEnd.clone();
    }

    // Store positions before snapping
    const originalMovedStart = finalMovedStart.clone();
    const originalMovedEnd = finalMovedEnd.clone();

    let snapped = false;
    let startSnapped = false;
    let endSnapped = false;

    // Middle handles should never affect wall connections - they only move walls perpendicular to their direction
    // No snapping to other objects for middle handles to prevent connection detachment

    // Generate alignment lines for current position BEFORE calculating snaps
    // This ensures the alignment candidates reflect the current wall position
    if (!visualFeedbackMode) {
        updateAlignmentLines(object.id, finalMovedStart.x, finalMovedStart.y, "middleV");
        updateAlignmentLines(object.id, finalMovedEnd.x, finalMovedEnd.y, "middleV");
    }

    // Alignment snapping uses alignment lines (generated above)
    const dragState = floorplannerStore.wallDragState.get(object.id);

    let startSnapResult = calculateAlignmentSnap(object, WallConnectionStart, finalMovedStart, {
        overrides: { start: finalMovedStart, end: finalMovedEnd },
        previous: dragState?.alignmentSnap?.start,
    });
    let endSnapResult = calculateAlignmentSnap(object, WallConnectionEnd, finalMovedEnd, {
        overrides: { start: finalMovedStart, end: finalMovedEnd },
        previous: dragState?.alignmentSnap?.end,
    });

    // For middle handles, we're translating the wall without changing its angle
    // No angle-based filtering needed - allow all alignment axes
    const applyStartX = startSnapResult.snappedX;
    const applyStartY = startSnapResult.snappedY;
    const applyEndX = endSnapResult.snappedX;
    const applyEndY = endSnapResult.snappedY;

    // Update snap results to reflect filtered axes
    startSnapResult.snappedX = applyStartX;
    startSnapResult.snappedY = applyStartY;
    startSnapResult.snapped = applyStartX || applyStartY;
    endSnapResult.snappedX = applyEndX;
    endSnapResult.snappedY = applyEndY;
    endSnapResult.snapped = applyEndX || applyEndY;

    startSnapped = startSnapResult.snapped;
    endSnapped = endSnapResult.snapped;

    if (startSnapped) {
        snapped = true;
        if (visualFeedbackMode) {
            // PERFORMANCE: Skip updateAlignmentLines - it iterates ALL walls causing O(n²) lag
            // updateAlignmentLines(object.id, finalMovedStart.x, finalMovedStart.y, "start");
        } else {
            // Normal mode: Calculate final adjustment
            const snappedStart = startSnapResult.start.clone();

            // Compute the adjustment vector
            const adjustment = snappedStart.clone().sub(originalMovedStart);

            // Validate that the raw adjustment is reasonable before applying
            const adjustmentMagnitude = adjustment.length();
            if (adjustmentMagnitude > MAX_DRAG_DELTA) {
                console.error(`dragMiddleVHandle: Rejecting start snap adjustment - magnitude ${adjustmentMagnitude.toFixed(2)}m exceeds ${MAX_DRAG_DELTA}m`);
                console.error('dragMiddleVHandle: Snap debug info:', {
                    wallId: object.id,
                    originalMovedStart: { x: originalMovedStart.x, y: originalMovedStart.y },
                    snappedStart: { x: snappedStart.x, y: snappedStart.y },
                    adjustment: { x: adjustment.x, y: adjustment.y },
                    startSnapResult: {
                        snappedX: startSnapResult.snappedX,
                        snappedY: startSnapResult.snappedY,
                        targetX: startSnapResult.targetX,
                        targetY: startSnapResult.targetY,
                    }
                });
                // Reject the snap and use original positions
                startSnapped = false;
                snapped = false;
            } else {
                // Project the adjustment onto the perpendicular direction
                const projectionLength = adjustment.dot(perpendicular);
                const adjustedNetAdjustment = perpendicular
                    .clone()
                    .multiplyScalar(projectionLength);

                // Validate the projected adjustment as well
                const projectedMagnitude = Math.abs(projectionLength);
                if (projectedMagnitude > MAX_DRAG_DELTA) {
                    console.error(`dragMiddleVHandle: Rejecting start snap adjustment - projected magnitude ${projectedMagnitude.toFixed(2)}m exceeds ${MAX_DRAG_DELTA}m`);
                    startSnapped = false;
                    snapped = false;
                } else {
                    // Adjust positions
                    finalMovedStart.copy(originalMovedStart).add(adjustedNetAdjustment);
                    finalMovedEnd.copy(originalMovedEnd).add(adjustedNetAdjustment);
                }
            }
        }
    } else if (endSnapped) {
        snapped = true;
        if (visualFeedbackMode) {
            // PERFORMANCE: Skip updateAlignmentLines - it iterates ALL walls causing O(n²) lag
            // updateAlignmentLines(object.id, finalMovedEnd.x, finalMovedEnd.y, "end");
        } else {
            // Normal mode: Calculate final adjustment
            const snappedEnd = endSnapResult.end.clone();

            // Compute the adjustment vector
            const adjustment = snappedEnd.clone().sub(originalMovedEnd);

            // Validate that the raw adjustment is reasonable before applying
            const adjustmentMagnitude = adjustment.length();
            if (adjustmentMagnitude > MAX_DRAG_DELTA) {
                console.error(`dragMiddleVHandle: Rejecting end snap adjustment - magnitude ${adjustmentMagnitude.toFixed(2)}m exceeds ${MAX_DRAG_DELTA}m`);
                console.error('dragMiddleVHandle: Snap debug info:', {
                    wallId: object.id,
                    originalMovedEnd: { x: originalMovedEnd.x, y: originalMovedEnd.y },
                    snappedEnd: { x: snappedEnd.x, y: snappedEnd.y },
                    adjustment: { x: adjustment.x, y: adjustment.y },
                    endSnapResult: {
                        snappedX: endSnapResult.snappedX,
                        snappedY: endSnapResult.snappedY,
                        targetX: endSnapResult.targetX,
                        targetY: endSnapResult.targetY,
                    }
                });
                // Reject the snap and use original positions
                endSnapped = false;
                snapped = false;
            } else {
                // Project the adjustment onto the perpendicular direction
                const projectionLength = adjustment.dot(perpendicular);
                const adjustedNetAdjustment = perpendicular
                    .clone()
                    .multiplyScalar(projectionLength);

                // Validate the projected adjustment as well
                const projectedMagnitude = Math.abs(projectionLength);
                if (projectedMagnitude > MAX_DRAG_DELTA) {
                    console.error(`dragMiddleVHandle: Rejecting end snap adjustment - projected magnitude ${projectedMagnitude.toFixed(2)}m exceeds ${MAX_DRAG_DELTA}m`);
                    endSnapped = false;
                    snapped = false;
                } else {
                    // Adjust positions
                    finalMovedStart.copy(originalMovedStart).add(adjustedNetAdjustment);
                    finalMovedEnd.copy(originalMovedEnd).add(adjustedNetAdjustment);
                }
            }
        }
    }

    // Final validation: Ensure final positions are still valid before calculating actualDelta
    // This catches any corruption that might have happened during snapping or constraint validation
    const finalPosValidation = validateWallDimensions(finalMovedStart, finalMovedEnd);
    if (!finalPosValidation.isValid) {
        console.error(`dragMiddleVHandle: Final positions are invalid before actualDelta calculation: ${finalPosValidation.error}`);
        console.error('dragMiddleVHandle: Final position debug info:', {
            wallId: object.id,
            finalMovedStart: { x: finalMovedStart.x, y: finalMovedStart.y },
            finalMovedEnd: { x: finalMovedEnd.x, y: finalMovedEnd.y },
            originalMovedStart: { x: originalMovedStart.x, y: originalMovedStart.y },
            originalMovedEnd: { x: originalMovedEnd.x, y: originalMovedEnd.y },
            objectStart: { x: object.start.x, y: object.start.y },
            objectEnd: { x: object.end.x, y: object.end.y },
            snapped,
            startSnapped,
            endSnapped,
        });
        // Reset to original moved positions (safe fallback)
        finalMovedStart.copy(originalMovedStart);
        finalMovedEnd.copy(originalMovedEnd);
    }

    // Also validate the object's current positions (they shouldn't be corrupted, but check anyway)
    const objectPosValidation = validateWallDimensions(object.start, object.end);
    if (!objectPosValidation.isValid) {
        console.error(`dragMiddleVHandle: Object positions are corrupted: ${objectPosValidation.error}`);
        console.error('dragMiddleVHandle: Object position debug info:', {
            wallId: object.id,
            objectStart: { x: object.start.x, y: object.start.y },
            objectEnd: { x: object.end.x, y: object.end.y },
        });
        // Can't proceed if object positions are corrupted - return early
        return false;
    }

    // Calculate the actual delta that was applied (after snapping adjustments)
    // Since middle handle drags move the entire wall, both endpoints move by the same delta
    const actualDelta = new THREE.Vector2()
        .subVectors(finalMovedStart, object.start);

    // Middle-handle drags translate the whole wall, so arc control points must
    // follow the actual applied translation, including snapped movement.
    const newControlPoint = object.controlPoint
        ? object.controlPoint.clone().add(actualDelta)
        : null;

    // Validate actualDelta - this is critical for preventing extreme values being applied to connected walls
    const actualDeltaValidation = validateDragDelta(actualDelta);
    if (!actualDeltaValidation.isValid) {
        console.error(`dragMiddleVHandle: actualDelta validation failed: ${actualDeltaValidation.error}`);
        console.error('dragMiddleVHandle: actualDelta debug info:', {
            wallId: object.id,
            actualDelta: { x: actualDelta.x, y: actualDelta.y },
            finalMovedStart: { x: finalMovedStart.x, y: finalMovedStart.y },
            finalMovedEnd: { x: finalMovedEnd.x, y: finalMovedEnd.y },
            objectStart: { x: object.start.x, y: object.start.y },
            objectEnd: { x: object.end.x, y: object.end.y },
            snapped,
            startSnapped,
            endSnapped,
        });
        // Return early to prevent applying corrupt delta to connected walls
        return false;
    }

    // Update connected object endpoints for edge-to-edge connections
    // When dragging an object by middle handle, if an endpoint is connected to another object's endpoint,
    // we should resize that connected object by moving its connected endpoint
    if (isWallType(object) || isSingleLineType(object)) {
        const edgeConnections = isWallType(object) ? (object as WallType).edgeConnections || [] : [];
        const centerConnections = object.centerConnections || [];
        const cascadeSourceIds = new Set<string>();

        // Helper to update connected object geometry during drag
        const updateConnectedObjectGeometry = (connectedObject: WallType | SingleLineType) => {
            // Skip geometry updates for objects that are not allowed to update during drag
            if (!DragStateManager.isWallAllowedToUpdate(connectedObject.id)) {
                return;
            }

            try {
                if (isWallType(connectedObject)) {
                    floorplannerStore.setObjectProperty(connectedObject.id, "cornerState", undefined);

                    if (connectedObject.symbolAttachments && connectedObject.symbolAttachments.length > 0) {
                        updateSymbolAttachmentsDuringDrag(connectedObject.id, floorplannerStore);
                    }

                    renderStore.composeWallShape(connectedObject.id, undefined, true);
                    // Skip trimShapes during drag - it's expensive and shapes may not be ready yet
                    // Trimming will happen automatically when drag ends via wallUpdateManager
                    // renderStore.trimShapes(connectedObject);
                    if (renderStore.isVisualUpdateAllowed(connectedObject.id)) {
                        renderStore.generateOutlineSegments(connectedObject.id);
                    }
                }
                renderStore.requestInvalidation(connectedObject.id);
            } catch (geometryError) {
                console.error("dragMiddleVHandle: Failed to update connected object geometry", {
                    objectId: connectedObject.id,
                    error: geometryError instanceof Error ? geometryError.message : geometryError,
                });
            }
        };

        const adjustConnectedObjectEndpoint = (connectedObject: WallType | SingleLineType, targetEndpoint: "start" | "end") => {
            // Skip updates for objects that are not allowed to update during drag
            if (!DragStateManager.isWallAllowedToUpdate(connectedObject.id)) {
                return;
            }

            if (targetEndpoint === "start") {
                const newStart = connectedObject.start.clone().add(actualDelta);
                floorplannerStore.updateObjectPosition(connectedObject.id, newStart, connectedObject.end, { bypassEdgeConstraints: true, skipInvalidation: true });
            } else {
                const newEnd = connectedObject.end.clone().add(actualDelta);
                floorplannerStore.updateObjectPosition(connectedObject.id, connectedObject.start, newEnd, { bypassEdgeConstraints: true, skipInvalidation: true });
            }
            cascadeSourceIds.add(connectedObject.id);
            if (isWallType(connectedObject)) {
                floorplannerStore.incrementWallGeometryVersion(connectedObject.id);
            }
            updateConnectedObjectGeometry(connectedObject);
        };

        const processEndpointConnections = (
            edgeSourceEndpoint: "start" | "end",
            centerSourcePosition: number
        ) => {
            const processed = new Set<string>();

            // Edge-based connections (only for walls)
            const endpointEdgeConnections = edgeConnections.filter(conn => conn.sourceEndpoint === edgeSourceEndpoint);
            endpointEdgeConnections.forEach(conn => {
                const connectedObject = floorplannerStore.findObjectId(conn.targetWallId);
                if (!connectedObject || (!isWallType(connectedObject) && !isSingleLineType(connectedObject))) {
                    return;
                }
                const targetEndpoint = conn.targetEndpoint === "start" || conn.targetEndpoint === "end"
                    ? conn.targetEndpoint
                    : null;
                const key = `${connectedObject.id}:${targetEndpoint ?? "center-edge"}`;
                if (processed.has(key)) {
                    return;
                }
                processed.add(key);
                if (targetEndpoint) {
                    adjustConnectedObjectEndpoint(connectedObject as WallType | SingleLineType, targetEndpoint);
                } else {
                    updateConnectedObjectGeometry(connectedObject as WallType | SingleLineType);
                }
            });

            // Center-based connections (works for both walls and single lines)
            const endpointCenterConnections = centerConnections.filter(conn => conn.sourcePosition === centerSourcePosition);
            endpointCenterConnections.forEach(conn => {
                let targetEndpoint: "start" | "end" | null = null;
                if (conn.targetPosition === WallConnectionStart) {
                    targetEndpoint = "start";
                } else if (conn.targetPosition === WallConnectionEnd) {
                    targetEndpoint = "end";
                }
                const connectedObject = floorplannerStore.findObjectId(conn.id);
                if (!connectedObject || (!isWallType(connectedObject) && !isSingleLineType(connectedObject))) {
                    return;
                }

                const key = `${connectedObject.id}:${targetEndpoint ?? "center"}`;
                if (processed.has(key)) {
                    return;
                }
                processed.add(key);
                if (targetEndpoint) {
                    adjustConnectedObjectEndpoint(connectedObject as WallType | SingleLineType, targetEndpoint);
                } else {
                    updateConnectedObjectGeometry(connectedObject as WallType | SingleLineType);
                }
            });
        };

        processEndpointConnections("start", WallConnectionStart);
        processEndpointConnections("end", WallConnectionEnd);

        // Handle middle connections (T-junctions)
        // When an object with a middle connection moves, the connected object should move with it
        // to maintain the connection point
        const middleConnections = centerConnections.filter(conn =>
            conn.sourcePosition !== WallConnectionStart &&
            conn.sourcePosition !== WallConnectionEnd
        );

        // Track processed objects to prevent double-updates when same object appears in multiple connections
        const processedMiddleObjects = new Set<string>();

        middleConnections.forEach(conn => {
            const connectedObject = floorplannerStore.findObjectId(conn.id);
            if (!connectedObject || (!isWallType(connectedObject) && !isSingleLineType(connectedObject))) {
                return;
            }

            // Skip if this object was already processed (prevents double-updates causing deformation)
            const key = `${connectedObject.id}:${conn.targetPosition}`;
            if (processedMiddleObjects.has(key)) {
                return;
            }
            processedMiddleObjects.add(key);

            // Skip updates for objects that are not allowed to update during drag
            if (!DragStateManager.isWallAllowedToUpdate(connectedObject.id)) {
                return;
            }

            // For middle connections, calculate the ACTUAL connection point based on where
            // the connected object is positioned, not just using sourcePosition (which might be WallConnectionMiddle)
            // This preserves the actual connection position even if sourcePosition is stored as 5000
            let actualConnectionPoint: THREE.Vector2;
            
            if (conn.targetPosition === WallConnectionStart) {
                // Connected at start - the connection point is where the connected object's start is
                actualConnectionPoint = connectedObject.start.clone();
            } else if (conn.targetPosition === WallConnectionEnd) {
                // Connected at end - the connection point is where the connected object's end is
                actualConnectionPoint = connectedObject.end.clone();
            } else {
                // Connected at middle - calculate based on the actual position
                // Project the connected object's midpoint onto the source object to find the connection point
                const connectedObjectMidpoint = new THREE.Vector2(
                    (connectedObject.start.x + connectedObject.end.x) / 2,
                    (connectedObject.start.y + connectedObject.end.y) / 2
                );
                // Project this point onto the source object's original centerline.
                // finalMovedStart/finalMovedEnd already include actualDelta, so using
                // them here and then adding actualDelta would apply the movement twice.
                const objectDirection = new THREE.Vector2()
                    .subVectors(object.end, object.start)
                    .normalize();
                const toMidpoint = new THREE.Vector2().subVectors(connectedObjectMidpoint, object.start);
                const projection = toMidpoint.dot(objectDirection);
                actualConnectionPoint = new THREE.Vector2().addVectors(
                    object.start,
                    objectDirection.clone().multiplyScalar(projection)
                );
            }

            // Now calculate where this connection point should be on the moved object
            // The connection point moves by actualDelta (since the object is being translated)
            const newConnectionPoint = actualConnectionPoint.clone().add(actualDelta);

            // Update the connected object based on where it's connected
            if (conn.targetPosition === WallConnectionStart) {
                // Connected at start - move the start point to the new connection point
                const newStart = newConnectionPoint.clone();
                floorplannerStore.updateObjectPosition(
                    connectedObject.id, 
                    newStart, 
                    connectedObject.end, 
                    { bypassEdgeConstraints: true, skipInvalidation: true }
                );
            } else if (conn.targetPosition === WallConnectionEnd) {
                // Connected at end - move the end point to the new connection point
                const newEnd = newConnectionPoint.clone();
                floorplannerStore.updateObjectPosition(
                    connectedObject.id, 
                    connectedObject.start, 
                    newEnd, 
                    { bypassEdgeConstraints: true, skipInvalidation: true }
                );
            } else {
                // Connected at middle - move the entire object by actualDelta to maintain relative position
                const newStart = connectedObject.start.clone().add(actualDelta);
                const newEnd = connectedObject.end.clone().add(actualDelta);
                floorplannerStore.updateObjectPosition(
                    connectedObject.id, 
                    newStart, 
                    newEnd, 
                    { bypassEdgeConstraints: true, skipInvalidation: true }
                );
            }

            // Update sourcePosition to the actual distance to preserve it for future operations
            const objectLength = finalMovedStart.distanceTo(finalMovedEnd);
            const finalDirection = new THREE.Vector2().subVectors(finalMovedEnd, finalMovedStart);
            const actualDistance = finalDirection.lengthSq() > 0
                ? new THREE.Vector2().subVectors(newConnectionPoint, finalMovedStart).dot(finalDirection.normalize())
                : 0;
            if (actualDistance >= 0 && actualDistance <= objectLength && 
                (conn.sourcePosition === WallConnectionMiddle || 
                 Math.abs(conn.sourcePosition - actualDistance) > 0.01)) {
                // Update sourcePosition to the actual distance if it's still WallConnectionMiddle
                // or if it's significantly different from the actual distance
                conn.sourcePosition = actualDistance;
            }

            if (isWallType(connectedObject)) {
                floorplannerStore.incrementWallGeometryVersion(connectedObject.id);
            }
            updateConnectedObjectGeometry(connectedObject as WallType | SingleLineType);
            cascadeSourceIds.add(connectedObject.id);
        });

        const cascadedConnectionIds = cascadeCenterConnectionUpdates(
            Array.from(cascadeSourceIds),
            floorplannerStore,
            new Set([object.id]),
        );
        cascadedConnectionIds.forEach(cascadedId => {
            const cascadedObject = floorplannerStore.findObjectId(cascadedId);
            if (!cascadedObject || (!isWallType(cascadedObject) && !isSingleLineType(cascadedObject))) {
                return;
            }
            if (isWallType(cascadedObject)) {
                floorplannerStore.incrementWallGeometryVersion(cascadedObject.id);
            }
            updateConnectedObjectGeometry(cascadedObject as WallType | SingleLineType);
        });
    }

    // Update all observables atomically to prevent multiple renders
    runInAction(() => {
        // Middle handles always translate the entire wall
        // The connection system will handle updating connected walls' endpoints
        // Skip invalidation during drag to prevent expensive cascading geometry updates
        floorplannerStore.updateObjectPosition(object.id, finalMovedStart, finalMovedEnd, { bypassEdgeConstraints: true, skipInvalidation: true });

        if (newControlPoint) {
            floorplannerStore.setObjectProperty(object.id, 'controlPoint', newControlPoint);
        }

        // Update geometry for the dragged wall so fills and outlines render correctly during drag
        if (isWallType(object)) {
            floorplannerStore.setObjectProperty(object.id, "cornerState", undefined);
            renderStore.composeWallShape(object.id, undefined, true);
            renderStore.trimShapes(object as WallType); // Don't force - let progressive mode skip if active
            if (renderStore.isVisualUpdateAllowed(object.id)) {
                renderStore.generateOutlineSegments(object.id);
            }

            // Update symbol attachments for the dragged wall itself
            const wall = object as WallType;
            if (wall.symbolAttachments && wall.symbolAttachments.length > 0) {
                updateSymbolAttachmentsDuringDrag(wall.id, floorplannerStore);
            }
        }

        // Force increment geometry version to invalidate cache
        floorplannerStore.incrementWallGeometryVersion(object.id);

        // Update drag state for hysteresis
        if (dragState) {
            if (!dragState.alignmentSnap) {
                dragState.alignmentSnap = {};
            }
            dragState.alignmentSnap.start = {
                snappedX: startSnapResult.snappedX,
                snappedY: startSnapResult.snappedY,
                targetX: startSnapResult.targetX,
                targetY: startSnapResult.targetY,
            };
            dragState.alignmentSnap.end = {
                snappedX: endSnapResult.snappedX,
                snappedY: endSnapResult.snappedY,
                targetX: endSnapResult.targetX,
                targetY: endSnapResult.targetY,
            };
        }
    });

    // NOTE: Connection updates are deferred until after drag ends.
    // During drag, wallUpdateManager's progressive mode handles visual updates.
    // Calling updateWallConnections here would trigger expensive cascading geometry
    // rebuilds that cause significant lag during middle handle drags.
    // Full connection updates happen automatically when drag ends via endWallDrag().

    // NOTE: We skip wallUpdateManager.updateWall calls during drag for performance.
    // These trigger expensive geometry calculations that are not needed during drag.
    // The geometry will be properly rebuilt when drag ends via endWallDrag().

    // Middle handles should never modify wall connections
    // Skip all connection updates to prevent inappropriate detachment during perpendicular movement

    delta.x = newDelta.x;
    delta.y = newDelta.y;
    return snapped;
}
